import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, EyeIcon } from 'lucide-react';
export function UserActivityAuditLog() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [username, setUsername] = useState('');
  const [activity, setActivity] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  // Sample activities
  const activities = [{
    id: '',
    name: 'All Activities'
  }, {
    id: 'login',
    name: 'Login'
  }, {
    id: 'logout',
    name: 'Logout'
  }, {
    id: 'create_user',
    name: 'Create User'
  }, {
    id: 'modify_user',
    name: 'Modify User'
  }, {
    id: 'delete_user',
    name: 'Delete User'
  }, {
    id: 'create_role',
    name: 'Create Role'
  }, {
    id: 'modify_role',
    name: 'Modify Role'
  }, {
    id: 'delete_role',
    name: 'Delete Role'
  }, {
    id: 'update_config',
    name: 'Update Configuration'
  }];
  // Sample users
  const users = [{
    id: '',
    name: 'All Users'
  }, {
    id: 'john.doe',
    name: 'John Doe'
  }, {
    id: 'jane.smith',
    name: 'Jane Smith'
  }, {
    id: 'admin',
    name: 'Admin User'
  }, {
    id: 'operator1',
    name: 'Operator 1'
  }, {
    id: 'manager1',
    name: 'Manager 1'
  }];
  // Sample audit log data
  const sampleAuditData = [{
    id: 1,
    timestamp: '2023-05-10 14:30:22',
    username: 'john.doe',
    userFullName: 'John Doe',
    activity: 'login',
    activityName: 'Login',
    ipAddress: '192.168.1.100',
    status: 'success',
    details: {
      browser: 'Chrome 113.0.0',
      os: 'Windows 10',
      location: 'Addis Ababa, Ethiopia'
    }
  }, {
    id: 2,
    timestamp: '2023-05-10 14:45:15',
    username: 'john.doe',
    userFullName: 'John Doe',
    activity: 'create_user',
    activityName: 'Create User',
    ipAddress: '192.168.1.100',
    status: 'success',
    details: {
      createdUser: 'new.user@example.com',
      role: 'Operator',
      actionBy: 'john.doe'
    }
  }, {
    id: 3,
    timestamp: '2023-05-10 15:10:47',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'modify_role',
    activityName: 'Modify Role',
    ipAddress: '192.168.1.101',
    status: 'success',
    details: {
      roleName: 'Operator',
      changes: 'Added new permissions',
      actionBy: 'jane.smith'
    }
  }, {
    id: 4,
    timestamp: '2023-05-10 15:30:33',
    username: 'admin',
    userFullName: 'Admin User',
    activity: 'update_config',
    activityName: 'Update Configuration',
    ipAddress: '192.168.1.102',
    status: 'success',
    details: {
      parameter: 'Password Policy',
      oldValue: '30 days',
      newValue: '90 days',
      actionBy: 'admin'
    }
  }, {
    id: 5,
    timestamp: '2023-05-10 16:15:19',
    username: 'john.doe',
    userFullName: 'John Doe',
    activity: 'logout',
    activityName: 'Logout',
    ipAddress: '192.168.1.100',
    status: 'success',
    details: {
      browser: 'Chrome 113.0.0',
      os: 'Windows 10',
      location: 'Addis Ababa, Ethiopia'
    }
  }, {
    id: 6,
    timestamp: '2023-05-09 09:30:22',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'login',
    activityName: 'Login',
    ipAddress: '192.168.1.101',
    status: 'failed',
    details: {
      browser: 'Firefox 112.0.0',
      os: 'Ubuntu 20.04',
      location: 'Addis Ababa, Ethiopia',
      reason: 'Invalid password'
    }
  }, {
    id: 7,
    timestamp: '2023-05-09 09:31:15',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'login',
    activityName: 'Login',
    ipAddress: '192.168.1.101',
    status: 'success',
    details: {
      browser: 'Firefox 112.0.0',
      os: 'Ubuntu 20.04',
      location: 'Addis Ababa, Ethiopia'
    }
  }, {
    id: 8,
    timestamp: '2023-05-09 10:45:33',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'delete_user',
    activityName: 'Delete User',
    ipAddress: '192.168.1.101',
    status: 'success',
    details: {
      deletedUser: 'old.user@example.com',
      actionBy: 'jane.smith',
      reason: 'No longer with company'
    }
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Validate date range
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        setIsLoading(false);
        return;
      }
      if (from > to) {
        alert('From date must be before To date');
        setIsLoading(false);
        return;
      }
    }
    // Simulate API delay
    setTimeout(() => {
      // Filter sample data based on search criteria
      let results = [...sampleAuditData];
      // Filter by username if selected
      if (username) {
        results = results.filter(log => log.username === username);
      }
      // Filter by activity if selected
      if (activity) {
        results = results.filter(log => log.activity === activity);
      }
      // Sort by timestamp (newest first)
      results.sort((a, b) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      setSearchResults(results);
      setHasSearched(true);
      setIsLoading(false);
    }, 800);
  };
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setUsername('');
    setActivity('');
    setSearchResults([]);
    setHasSearched(false);
  };
  const handleExport = (format: 'csv' | 'pdf') => {
    // In a real app, you would implement the actual export functionality
    alert(`Exporting as ${format.toUpperCase()}...`);
  };
  const handleViewDetails = (log: any) => {
    setSelectedLog(log);
    setShowDetails(true);
  };
  return <div className="space-y-6" data-id="element-1412">
      <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1413">
        User Activity Audit Log
      </h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-1414">
        <form onSubmit={handleSearch} className="space-y-6" data-id="element-1415">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1416">
            <div className="space-y-2" data-id="element-1417">
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700" data-id="element-1418">
                From Date
              </label>
              <input id="fromDate" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1419" />
            </div>
            <div className="space-y-2" data-id="element-1420">
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700" data-id="element-1421">
                To Date
              </label>
              <input id="toDate" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1422" />
            </div>
            <div className="space-y-2" data-id="element-1423">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700" data-id="element-1424">
                User
              </label>
              <select id="username" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1425">
                {users.map(user => <option key={user.id} value={user.id} data-id="element-1426">
                    {user.name}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-1427">
              <label htmlFor="activity" className="block text-sm font-medium text-gray-700" data-id="element-1428">
                Activity
              </label>
              <select id="activity" value={activity} onChange={e => setActivity(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1429">
                {activities.map(act => <option key={act.id} value={act.id} data-id="element-1430">
                    {act.name}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1431">
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-[#F58220] text-white rounded-md font-medium hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50 flex items-center" data-id="element-1432">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1433">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1434"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1435"></path>
                  </svg>
                  Generating...
                </> : <>
                  <SearchIcon size={16} className="inline mr-2" data-id="element-1436" />
                  Generate Report
                </>}
            </button>
            <button type="button" onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50" data-id="element-1437">
              Reset
            </button>
          </div>
        </form>
      </div>
      {hasSearched && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1438">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1439">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-1440">Audit Log Results</h3>
            {searchResults.length > 0 && <div className="flex items-center gap-3" data-id="element-1441">
                <button onClick={() => handleExport('csv')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1442">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1443" />
                  Export CSV
                </button>
                <button onClick={() => handleExport('pdf')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1444">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1445" />
                  Export PDF
                </button>
              </div>}
          </div>
          {searchResults.length === 0 ? <div className="p-8 text-center" data-id="element-1446">
              <p className="text-gray-500" data-id="element-1447">
                No audit logs found matching your search criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1448">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1449">
                <thead className="bg-gray-50" data-id="element-1450">
                  <tr data-id="element-1451">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1452">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1453">
                      User
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1454">
                      Activity
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1455">
                      IP Address
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1456">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1457">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1458">
                  {searchResults.map(log => <tr key={log.id} className="hover:bg-gray-50" data-id="element-1459">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1460">
                        {log.timestamp}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1461">
                        {log.userFullName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1462">
                        {log.activityName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1463">
                        {log.ipAddress}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1464">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1465">
                          {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1466">
                        <button onClick={() => handleViewDetails(log)} className="text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1467">
                          <EyeIcon size={16} className="mr-1" data-id="element-1468" />
                          View
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
      {/* Details Modal */}
      {showDetails && selectedLog && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1469">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6" data-id="element-1470">
            <div className="flex justify-between items-center mb-4" data-id="element-1471">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-1472">
                Activity Details
              </h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1473">
                &times;
              </button>
            </div>
            <div className="space-y-4" data-id="element-1474">
              <div className="grid grid-cols-2 gap-4" data-id="element-1475">
                <div data-id="element-1476">
                  <p className="text-sm text-gray-500" data-id="element-1477">Date & Time</p>
                  <p className="text-sm font-medium" data-id="element-1478">{selectedLog.timestamp}</p>
                </div>
                <div data-id="element-1479">
                  <p className="text-sm text-gray-500" data-id="element-1480">User</p>
                  <p className="text-sm font-medium" data-id="element-1481">
                    {selectedLog.userFullName}
                  </p>
                </div>
                <div data-id="element-1482">
                  <p className="text-sm text-gray-500" data-id="element-1483">Activity</p>
                  <p className="text-sm font-medium" data-id="element-1484">
                    {selectedLog.activityName}
                  </p>
                </div>
                <div data-id="element-1485">
                  <p className="text-sm text-gray-500" data-id="element-1486">IP Address</p>
                  <p className="text-sm font-medium" data-id="element-1487">{selectedLog.ipAddress}</p>
                </div>
                <div data-id="element-1488">
                  <p className="text-sm text-gray-500" data-id="element-1489">Status</p>
                  <p className={`text-sm font-medium ${selectedLog.status === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-1490">
                    {selectedLog.status.charAt(0).toUpperCase() + selectedLog.status.slice(1)}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4" data-id="element-1491">
                <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-1492">
                  Additional Details
                </p>
                <div className="bg-gray-50 p-3 rounded-md" data-id="element-1493">
                  <pre className="text-xs overflow-auto" data-id="element-1494">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-1495">
              <button onClick={() => setShowDetails(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300" data-id="element-1496">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
}